import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createWish } from '../../services/wishesApi';
import { validateWishForm } from '../../utils/validation';
import styles from './MobileWishForm.module.css';

interface MobileWishFormProps {
  initialData?: {
    title?: string;
    description?: string;
    category?: string;
    region?: string;
  };
}

const MobileWishForm = ({ initialData }: MobileWishFormProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || '',
    region: initialData?.region || '',
    district: '',
    budget: '',
    urgent: false,
    userName: '',
    userPhone: '',
    userEmail: '',
  });

  const [, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = ['維修', '保養', '美容', '改裝'];
  const regions = ['台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, value, type } = target;
    const checked = (target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateWishForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);

    try {
      const wishData = {
        title: formData.title,
        description: formData.description || undefined,
        category: formData.category,
        region: formData.region,
        district: formData.district || undefined,
        budget: formData.budget || undefined,
        urgent: formData.urgent,
        userName: formData.userName,
        userPhone: formData.userPhone || undefined,
        userEmail: formData.userEmail || undefined,
      };

      const newWish = await createWish(wishData);

      if (newWish) {
        navigate(`/wishes/${newWish.id}`);
      }
    } catch (error) {
      console.error('Failed to create wish:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.field}>
        <label className={styles.label}>標題 *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className={styles.input}
          placeholder="請輸入標題"
          required
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>描述</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className={styles.textarea}
          placeholder="請詳細描述您的需求"
          rows={4}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>類別 *</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          className={styles.select}
          required
        >
          <option value="">請選擇</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>地區 *</label>
        <select
          name="region"
          value={formData.region}
          onChange={handleChange}
          className={styles.select}
          required
        >
          <option value="">請選擇</option>
          {regions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            name="urgent"
            checked={formData.urgent}
            onChange={handleChange}
            className={styles.checkbox}
          />
          <span>急件</span>
        </label>
      </div>

      <div className={styles.section}>
        <div className={styles.field}>
          <label className={styles.label}>聯絡人姓名 *</label>
          <input
            type="text"
            name="userName"
            value={formData.userName}
            onChange={handleChange}
            className={styles.input}
            placeholder="請輸入姓名"
            required
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>聯絡電話</label>
          <input
            type="tel"
            name="userPhone"
            value={formData.userPhone}
            onChange={handleChange}
            className={styles.input}
            placeholder="0912-345-678"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Email</label>
          <input
            type="email"
            name="userEmail"
            value={formData.userEmail}
            onChange={handleChange}
            className={styles.input}
            placeholder="your@email.com"
          />
        </div>
      </div>

      <button type="submit" className={styles.submit} disabled={isSubmitting}>
        {isSubmitting ? '發布中...' : '發布許願'}
      </button>
    </form>
  );
};

export default MobileWishForm;
